"use client";

/**
 * Teaches share-target usage to users who launched the app from their
 * home screen. Without this, a fresh PWA user stares at the hero URL
 * input and doesn't realise the entire point of having installed the
 * app is that they can skip the URL input via the OS share menu.
 *
 * Lifecycle:
 *   - Only rendered when running as a PWA (display-mode standalone).
 *   - Starts as a small "How to use" pill - never auto-expands. The
 *     first-launch takeover (full-screen welcome modal + expanded
 *     card) was removed as entry noise; teaching is opt-in now.
 *   - Pill stays available forever (no full dismissal) so returning
 *     users who forgot the flow can re-expand it anytime.
 *   - State persists in localStorage so "expanded" survives reloads.
 */
import { useCallback, useEffect, useState } from "react";
import { TAG } from "@/lib/ui-classes";
import { Lightbulb, Share as ShareIcon, X, ChevronDown, ChevronUp } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { useIsPWA } from "@/lib/use-install-prompt";

const COLLAPSED_KEY = "menzil-pwa-helper-collapsed";

export default function PWAHomeHelper() {
  const t = useT();
  const isPWA = useIsPWA();
  const [collapsed, setCollapsed] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      // Collapsed unless the user explicitly expanded it ("0").
      setCollapsed(localStorage.getItem(COLLAPSED_KEY) !== "0");
    } catch {
      setCollapsed(true);
    }
  }, []);

  const collapse = useCallback(() => {
    setCollapsed(true);
    try { localStorage.setItem(COLLAPSED_KEY, "1"); } catch { /* ignore */ }
  }, []);

  const expand = useCallback(() => {
    setCollapsed(false);
    try { localStorage.setItem(COLLAPSED_KEY, "0"); } catch { /* ignore */ }
  }, []);

  // Hide until both gates resolve: isPWA (only PWA users get this
  // surface) and collapsed (hydrated from storage after mount).
  if (!isPWA || collapsed === null) return null;

  if (collapsed) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <button
          type="button"
          onClick={expand}
          className="w-full sm:w-auto inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-gray-300 transition-colors"
          aria-expanded="false"
        >
          <Lightbulb className="h-3.5 w-3.5 text-gray-500" />
          {t("pwa.helper.collapsed.label")}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
      {/* A plain white card with the hairline border (the quiet UI):
          the grey tokens flip it for the dark theme. */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100">
            <Lightbulb className="h-5 w-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
              {t("pwa.helper.title")}
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">
              {t("pwa.helper.intro")}
            </p>

            <ol className="mt-3 space-y-2 text-xs sm:text-sm text-gray-700">
              <HelperStep num={1} body={t("pwa.helper.step1")} />
              <HelperStep
                num={2}
                body={t("pwa.helper.step2")}
                icon={<ShareIcon className="h-3.5 w-3.5 text-gray-500" />}
              />
              <HelperStep num={3} body={t("pwa.helper.step3")} />
            </ol>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={collapse}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300"
              >
                {t("pwa.helper.gotit")}
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={collapse}
            aria-label={t("pwa.helper.gotit")}
            className="rounded-lg p-1 text-gray-400 hover:bg-white/60 dark:hover:bg-white/10 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function HelperStep({
  num, body, icon,
}: { num: number; body: string; icon?: React.ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${TAG}`}>
        {num}
      </span>
      <span className="flex-1 flex items-center gap-1.5 leading-snug">
        {icon}
        <span>{body}</span>
      </span>
    </li>
  );
}
